import { type FirstLevelItemProps } from '@pixum/web-navigation';
import { type NavigationNode } from 'data-layer';
import { JENTIS_PENDING_IDENTIFIER, SessionStorage } from 'utils';
import { getCurrentUrl } from '@pixum/auth-core-ui';

/**
 * Returns a new array of navigation items with onClick handlers.
 *
 * This function creates a new array and processes the provided navigation items by adding
 * an `onClick` handler to each item. The `onClick` handler saves tracking data to session
 * storage and redirects to the item's URL if it is masked and has a URL defined.
 *
 * If a navigation item contains nested items, the function is called recursively to ensure
 * all nested items are processed.
 *
 * @param navigationItems - The list of navigation items to process. Each item may contain
 *                          nested items and properties such as `isMasked` and `url`.
 * @returns A new array with onClick handlers added, without mutating the input
 */
export function addClickEventToNavigationItems(
  navigationItems: FirstLevelItemProps[]
): FirstLevelItemProps[] {
  return navigationItems.map(item => {
    const withClick: FirstLevelItemProps = {
      ...item,
      onClick: () => {
        const currentUrl = getCurrentUrl();
        SessionStorage.setItem(
          JENTIS_PENDING_IDENTIFIER,
          JSON.stringify({ module: `Navigation-${item.title}`, target: currentUrl })
        );

        if (item.isMasked && item.url) {
          window.location.assign(item.url as string);
        }
      },
    };

    if (item.items && item.items.length > 0) {
      return {
        ...withClick,
        // SecondLevelItemProps has no `items` field, so recursion terminates after one level
        items: addClickEventToNavigationItems(
          item.items as unknown as FirstLevelItemProps[]
        ) as unknown as typeof item.items,
      };
    }

    return withClick;
  });
}

/**
 * Transform the navigation node to the first level item props for the WebHeader component
 *
 * @param navigationNode
 * @returns
 */
export function transformNavigationNode(navigationNode: NavigationNode | undefined): FirstLevelItemProps[] {
  if (!navigationNode) {
    return [];
  }

  return navigationNode.children.map((firstLevelItem: NavigationNode) => ({
    backText: firstLevelItem.backTitle,
    title: firstLevelItem.title,
    text: firstLevelItem.title,
    url: firstLevelItem.link,
    isMasked: firstLevelItem.isMasked,
    isActive: firstLevelItem.isActive,
    onlyMobile: firstLevelItem.onlyMobile,
    items: firstLevelItem.children.map((secondLevelItem: NavigationNode) => ({
      parentTitle: firstLevelItem.title,
      title: secondLevelItem.title,
      text: secondLevelItem.title,
      url: secondLevelItem.link,
      isMasked: secondLevelItem.isMasked,
      isActive: secondLevelItem.isActive,
      onlyMobile: secondLevelItem.onlyMobile,
      image: secondLevelItem.image
        ? {
            alt: secondLevelItem.image?.alt || '',
            src: secondLevelItem.image?.url || '',
          }
        : undefined,
    })),
  }));
}
